// Thematic content for SEO-optimized pages

export const thematicContent = {
  nutritionHormonale: {
    meta: {
      title: "Nutritionniste spécialiste en nutrition hormonale | Vevey, Clarens & Montreux",
      description: "Nutritionniste à Vevey, Clarens et Montreux — accompagnement personnalisé en nutrition hormonale féminine : cycles, préménopause, SOPK, fatigue, prise de poids. Consultations remboursées.",
      keywords: "nutritionniste Vevey, nutritionniste Clarens, nutritionniste Montreux, nutrition hormonale, équilibre hormonal femme, Riviera"
    },
    h1: "Nutrition hormonale à Vevey, Clarens et Montreux",
    intro: "Votre corps change, vos cycles se dérèglent, votre poids ne répond plus comme avant ? Ces signes ne sont jamais anodins — ils reflètent un déséquilibre hormonal que seule une approche personnalisée peut vraiment corriger. Nutritionniste sur la Riviera vaudoise, nous accompagnons chaque femme avec une stratégie nutritionnelle adaptée à son profil unique.",
    sections: [
      {
        title: "Ce que la nutrition hormonale peut changer pour vous",
        content: "La nutrition hormonale ne consiste pas à \"manger sain\" au sens large. C'est une approche clinique qui reconnaît que votre alimentation influence directement vos hormones — œstrogènes, progestérone, insuline, cortisol, hormones thyroïdiennes — et donc votre poids, votre énergie, votre humeur, vos cycles et votre fertilité. Au Nutri Centre Riviera, nous travaillons à Vevey, Clarens et Montreux avec une exigence : comprendre votre profil hormonal avant toute recommandation."
      },
      {
        title: "Vous vous reconnaissez dans ces signes ?",
        list: [
          "Prise de poids, en particulier au niveau du ventre, sans changement d'habitudes",
          "Cycles menstruels irréguliers, douloureux ou absents",
          "Fatigue chronique malgré un sommeil suffisant",
          "Compulsions sucrées, surtout en fin de journée ou avant les règles",
          "Acné hormonale persistante, chute de cheveux, pilosité excessive",
          "Sautes d'humeur, anxiété, irritabilité inhabituelles",
          "Rétention d'eau, seins sensibles, migraines cycliques"
        ]
      },
      {
        title: "Notre approche à Vevey, Clarens et Montreux",
        subsections: [
          {
            subtitle: "Un bilan complet, pas des suppositions",
            text: "Anamnèse approfondie, bilan sanguin ciblé, analyse corporelle et bio-impédancemétrie : nous posons un diagnostic nutritionnel précis avant d'agir. Un conseil qui n'est pas fondé sur votre profil réel est rarement efficace."
          },
          {
            subtitle: "Un accompagnement sur mesure",
            text: "Aucune femme n'a la même histoire hormonale. Nous construisons une stratégie adaptée à votre rythme de vie, à vos contraintes et à vos objectifs — sans régime restrictif ni règles absurdes."
          },
          {
            subtitle: "Un suivi dans la durée",
            text: "L'équilibre hormonal ne se retrouve pas en trois semaines. Nous vous accompagnons consultation après consultation pour ajuster, encourager et installer des résultats durables."
          }
        ]
      },
      {
        title: "Ce sur quoi nous travaillons avec vous",
        pillars: [
          {
            name: "La stabilité glycémique",
            description: "Pierre angulaire de l'équilibre hormonal. Des variations glycémiques fréquentes dérèglent l'insuline, le cortisol, et favorisent le stockage abdominal."
          },
          {
            name: "Les micronutriments essentiels",
            description: "Magnésium, zinc, vitamines B, oméga-3, fer, vitamine D : des carences fréquentes chez la femme, rarement investigées, qui impactent directement vos hormones."
          },
          {
            name: "La détoxification hormonale",
            description: "Votre foie et votre microbiote éliminent les hormones en excès. Un soutien ciblé change radicalement le tableau hormonal."
          },
          {
            name: "L'inflammation chronique",
            description: "Souvent silencieuse, elle aggrave presque tous les déséquilibres hormonaux. Une approche anti-inflammatoire personnalisée est un levier majeur."
          }
        ]
      },
      {
        title: "Pour qui est cet accompagnement ?",
        conditions: [
          "SOPK (syndrome des ovaires polykystiques)",
          "Troubles du cycle menstruel",
          "Préménopause, ménopause",
          "Endométriose, douleurs cycliques",
          "Fatigue hormonale",
          "Prise de poids inexpliquée, résistance à la perte de poids",
          "Acné hormonale adulte",
          "Post-partum, post-pilule"
        ]
      }
    ],
    cta: {
      title: "Prenez rendez-vous à Vevey, Clarens ou Montreux",
      text: "Un premier échange permet de comprendre votre situation et de poser les bases d'un accompagnement sur mesure. Consultations remboursées par certaines assurances complémentaires (ASCA & RME).",
      button: "Réserver ma consultation"
    }
  },

  pertePoidsHormonale: {
    meta: {
      title: "Perte de poids durable à Vevey, Clarens & Montreux | Nutritionniste Riviera",
      description: "Vous n'arrivez pas à perdre du poids malgré vos efforts ? Accompagnement nutritionnel personnalisé à Vevey, Clarens et Montreux. Résultats durables, approche hormonale.",
      keywords: "perte de poids Vevey, perte de poids Clarens, perte de poids Montreux, nutritionniste Riviera, maigrir durablement, perte de poids hormonale"
    },
    h1: "Perte de poids à Vevey, Clarens et Montreux : une approche qui tient compte de vos hormones",
    intro: "Vous comptez vos calories, vous faites du sport, vous mangez sainement — et pourtant la balance ne bouge pas. Cette frustration est réelle, et elle n'est pas une question de volonté. Quand la perte de poids devient aussi difficile, c'est souvent parce que vos hormones ne répondent plus comme avant. Sur la Riviera vaudoise, nous vous accompagnons avec une approche personnalisée qui s'attaque aux vraies causes.",
    sections: [
      {
        title: "Pourquoi la perte de poids ne dépend pas que des calories",
        content: "Après 35-40 ans, après une grossesse, après un choc émotionnel, après l'arrêt d'une pilule — ou tout simplement sans raison apparente — le métabolisme féminin se transforme. Un déficit calorique ne suffit plus : il peut même aggraver la situation. Pour obtenir une perte de poids durable, il faut comprendre ce qui se joue dans votre corps. Notre centre à Vevey, Clarens et Montreux est spécialisé dans cette approche hormonale de la perte de poids, bien au-delà du simple comptage de calories."
      },
      {
        title: "Les hormones qui bloquent souvent la perte de poids",
        hormones: [
          {
            name: "L'insuline",
            effect: "Une résistance à l'insuline — fréquente et souvent non diagnostiquée — favorise le stockage abdominal et rend la perte de graisse presque impossible sans une stratégie adaptée."
          },
          {
            name: "Le cortisol",
            effect: "L'hormone du stress, quand elle reste chroniquement élevée, bloque la perte de poids et déclenche des fringales sucrées répétées."
          },
          {
            name: "Les hormones thyroïdiennes",
            effect: "Une thyroïde fatiguée — parfois infraclinique — ralentit tout le métabolisme. Les régimes classiques l'aggravent souvent."
          },
          {
            name: "Œstrogènes & progestérone",
            effect: "Leur déséquilibre, très fréquent en préménopause ou en post-partum, redistribue les graisses et brouille les signaux de satiété."
          },
          {
            name: "Leptine & ghréline",
            effect: "Les hormones de la faim et de la satiété se dérèglent avec le manque de sommeil, le stress et les régimes répétés — et créent des compulsions incontrôlables."
          }
        ]
      },
      {
        title: "Notre méthode pour une perte de poids durable",
        steps: [
          {
            title: "Étape 1 — Le bilan complet",
            items: [
              "Anamnèse approfondie : historique pondéral, hormonal, alimentaire",
              "Analyse corporelle (composition, masse grasse, masse musculaire, eau)",
              "Bio-impédancemétrie pour mesurer l'état métabolique réel",
              "Identification des freins hormonaux et des carences potentielles"
            ]
          },
          {
            title: "Étape 2 — Une stratégie nutritionnelle sur mesure",
            description: "Pas de régime restrictif, pas de menu standardisé. Nous construisons avec vous une stratégie qui stabilise votre glycémie, soutient votre métabolisme et respecte votre vie — avec l'application Nutrium pour un suivi fluide entre les séances."
          },
          {
            title: "Étape 3 — Un suivi pour garantir les résultats",
            description: "La perte de poids hormonale demande du temps et des ajustements. Nous vous accompagnons avec des consultations régulières pour célébrer les progrès, corriger ce qui bloque, et installer un équilibre durable — sans effet yo-yo."
          }
        ]
      },
      {
        title: "Ce qui nous différencie sur la Riviera",
        benefits: [
          "Une approche hormonale, pas un comptage de calories",
          "Un accompagnement 100% personnalisé, jamais de régime standardisé",
          "Un suivi dans la durée — l'objectif est votre équilibre à long terme",
          "Des consultations remboursées par certaines assurances (ASCA & RME)"
        ]
      },
      {
        title: "Ce que les personnes accompagnées constatent",
        results: [
          "Une perte de poids progressive, sans frustration ni effet yo-yo",
          "Une énergie retrouvée dès les premières semaines",
          "Une nette diminution des fringales et compulsions",
          "Un meilleur sommeil et une humeur plus stable",
          "Un corps qui redevient \"à l'écoute\" de ses vrais signaux",
          "Un équilibre hormonal qui se rétablit progressivement"
        ]
      }
    ],
    cta: {
      title: "Prête à comprendre ce qui bloque ?",
      text: "Réservez votre consultation au cabinet de Vevey ou de Clarens (Montreux). Un premier échange permet de poser les bases d'un accompagnement efficace et personnalisé — remboursé par certaines assurances complémentaires (ASCA & RME).",
      button: "Prendre rendez-vous"
    }
  },

  sopkAlimentation: {
    meta: {
      title: "SOPK & alimentation à Vevey, Clarens & Montreux | Nutritionniste spécialisée",
      description: "Vous vivez avec un SOPK ? Accompagnement nutritionnel personnalisé à Vevey, Clarens et Montreux pour rééquilibrer vos hormones. Approche adaptée à votre profil SOPK unique.",
      keywords: "SOPK Vevey, SOPK Clarens, SOPK Montreux, syndrome ovaires polykystiques Riviera, nutritionniste SOPK"
    },
    h1: "SOPK à Vevey, Clarens et Montreux : une approche personnalisée pour rééquilibrer vos hormones",
    intro: "Le SOPK (syndrome des ovaires polykystiques) touche environ une femme sur dix, mais s'exprime différemment chez chacune. C'est pourquoi les conseils génériques trouvés sur internet donnent rarement des résultats durables. Au Nutri Centre Riviera (Vevey, Clarens et Montreux), nous vous accompagnons avec une approche adaptée à votre profil SOPK unique.",
    sections: [
      {
        title: "Qu'est-ce que le SOPK (syndrome des ovaires polykystiques) ?",
        content: "Le SOPK est un trouble hormonal complexe qui se caractérise par des cycles irréguliers, un excès d'androgènes et une résistance à l'insuline dans environ 70 % des cas. Mais derrière ce diagnostic se cachent en réalité plusieurs profils — insulinorésistant, hyperandrogénique, post-pilule, inflammatoire, ou lié au stress — qui demandent chacun une stratégie différente. C'est ce qui explique pourquoi les régimes standardisés échouent si souvent.",
        symptoms: [
          "Cycles menstruels irréguliers, rares ou absents",
          "Prise de poids difficile à contrôler, surtout au niveau du ventre",
          "Acné persistante, pilosité excessive, chute de cheveux",
          "Fringales sucrées impossibles à ignorer",
          "Fatigue chronique, coups de pompe en fin de journée",
          "Difficultés à concevoir",
          "Anxiété, irritabilité, sautes d'humeur"
        ]
      },
      {
        title: "Pourquoi une approche personnalisée change tout",
        reasons: [
          {
            title: "1. Identifier votre profil SOPK",
            content: "Insulinorésistance marquée, excès d'androgènes, SOPK post-pilule, SOPK inflammatoire, SOPK lié au stress… Chaque profil demande une stratégie différente. Ce qui apaise l'une peut aggraver l'autre — c'est pourquoi un bilan précis est indispensable avant toute recommandation alimentaire."
          },
          {
            title: "2. Agir sur la vraie cause, pas uniquement sur les symptômes",
            content: "Couper le sucre ne suffit pas. Selon votre profil, l'accent sera mis sur l'insulinorésistance, l'inflammation, la détoxification hormonale, le soutien des surrénales ou la régulation du microbiote. Notre rôle est de poser un diagnostic clair et d'agir à la racine."
          },
          {
            title: "3. Construire un plan réaliste et durable",
            content: "Un plan trop strict ou calqué sur un protocole trouvé en ligne ne tient jamais dans le temps. Nous construisons avec vous une approche progressive, tenant compte de votre rythme, de vos goûts, et de vos contraintes — pour des résultats qui s'installent pour de bon."
          }
        ]
      },
      {
        title: "Notre programme SOPK à Vevey, Clarens et Montreux",
        program: [
          {
            phase: "Consultation initiale",
            description: "Anamnèse complète, bilan corporel, analyse de vos examens médicaux et définition d'objectifs réalistes."
          },
          {
            phase: "Stratégie nutritionnelle personnalisée",
            description: "Un plan adapté à votre profil SOPK, à votre vie et à vos contraintes — accompagné d'un suivi via l'application Nutrium."
          },
          {
            phase: "Suivi régulier",
            description: "Ajustements selon l'évolution de vos cycles, vos symptômes et votre énergie. Possibilité de collaboration avec votre gynécologue."
          }
        ]
      },
      {
        title: "Les principes nutritionnels que nous adaptons à votre profil",
        toEat: [
          "Une stabilité glycémique optimale, pilier de l'équilibre hormonal",
          "Des protéines de qualité à chaque repas, pour la satiété et les hormones",
          "Des graisses anti-inflammatoires (oméga-3) ciblées selon vos besoins",
          "Des fibres végétales pour nourrir votre microbiote et votre foie",
          "Des micronutriments clés souvent déficitaires (magnésium, zinc, vitamine D)",
          "Un rythme alimentaire respectueux de votre cortisol et de votre énergie",
          "Une hydratation adéquate pour la détoxification hormonale"
        ],
        toLimit: [
          "Les pics glycémiques répétés (ceux que chaque profil doit apprendre à identifier)",
          "Les aliments ultra-transformés qui entretiennent l'inflammation",
          "Les régimes restrictifs qui épuisent les surrénales",
          "Le jeûne imposé sans accompagnement",
          "Les compléments pris au hasard, qui peuvent masquer un déséquilibre",
          "Les conseils génériques appliqués sans bilan préalable"
        ]
      },
      {
        title: "Ce que les personnes accompagnées avec SOPK constatent",
        results: [
          "Une régularisation progressive du cycle menstruel",
          "Une perte de poids durable et sans frustration",
          "Une peau et des cheveux qui retrouvent leur équilibre",
          "Une diminution nette des fringales sucrées",
          "Une énergie retrouvée, un meilleur sommeil",
          "Une meilleure fertilité et un rapport apaisé à leur corps"
        ]
      }
    ],
    cta: {
      title: "Prête à comprendre votre SOPK ?",
      text: "Consultations au cabinet de Vevey ou de Clarens (Montreux). Un premier rendez-vous permet de poser les bases d'un accompagnement personnalisé. Consultations remboursées par certaines assurances complémentaires (ASCA & RME).",
      button: "Réserver ma consultation SOPK"
    }
  },

  premenopauseMetabolisme: {
    meta: {
      title: "Préménopause à Vevey, Clarens & Montreux | Nutritionniste spécialisée",
      description: "Préménopause, prise de poids, fatigue, bouffées de chaleur ? Accompagnement personnalisé à Vevey, Clarens et Montreux pour traverser cette transition sereinement.",
      keywords: "préménopause Vevey, ménopause Clarens, métabolisme femme 40 ans, nutritionniste Montreux, transition hormonale Riviera"
    },
    h1: "Préménopause à Vevey, Clarens et Montreux : traverser cette transition sereinement",
    intro: "Vous avez l'impression que votre corps ne répond plus comme avant ? Prise de poids inexpliquée, fatigue persistante, irritabilité, sommeil perturbé… La préménopause transforme profondément le métabolisme féminin, et peu de femmes sont préparées à comprendre ce qui se joue. Sur la Riviera vaudoise, nous vous accompagnons avec une approche adaptée à cette étape unique de votre vie.",
    sections: [
      {
        title: "Quand le corps change sans prévenir",
        content: "La préménopause débute en moyenne entre 40 et 50 ans, et peut durer plusieurs années. Pendant cette période, les fluctuations hormonales transforment progressivement le métabolisme, la répartition des graisses, l'énergie, l'humeur et le sommeil. Ce qui fonctionnait il y a 10 ans ne fonctionne plus — et c'est précisément ce qui rend les régimes habituels inefficaces."
      },
      {
        title: "Vous vous reconnaissez si…",
        list: [
          "Vous prenez du poids sans avoir changé vos habitudes — surtout au niveau du ventre",
          "Votre énergie a baissé, vous récupérez moins bien",
          "Votre sommeil est plus léger, avec parfois des réveils nocturnes",
          "Vous ressentez une irritabilité, des sautes d'humeur, une charge mentale plus lourde",
          "Vous avez des bouffées de chaleur ou des sueurs nocturnes",
          "Vos cycles deviennent irréguliers, plus courts ou plus abondants",
          "Vous avez essayé plusieurs régimes — sans résultat durable",
          "Vous avez l'impression que votre corps ne vous appartient plus"
        ]
      },
      {
        title: "Pourquoi la préménopause est plus complexe qu'on ne le croit",
        subsections: [
          {
            subtitle: "Plusieurs hormones en mouvement",
            text: "Œstrogènes, progestérone, cortisol, thyroïde, insuline… leurs déséquilibres simultanés expliquent pourquoi une simple modification alimentaire ne suffit presque jamais."
          },
          {
            subtitle: "Un métabolisme transformé",
            text: "La masse musculaire diminue, la sensibilité à l'insuline change, le stockage des graisses se réorganise. Le corps a besoin d'une stratégie nouvelle, adaptée à cette étape."
          },
          {
            subtitle: "Une dimension émotionnelle",
            text: "Cette transition peut éveiller des questionnements profonds. L'accompagnement va au-delà de la nutrition — il prend en compte votre vécu, vos émotions, votre rapport au corps."
          }
        ]
      },
      {
        title: "Notre approche à Vevey, Clarens et Montreux",
        pillars: [
          {
            name: "Comprendre votre profil hormonal",
            description: "Anamnèse approfondie, bilan sanguin ciblé, analyse corporelle et bio-impédancemétrie : avant toute recommandation, nous posons un point de départ précis."
          },
          {
            name: "Construire une stratégie sur mesure",
            description: "Pas de protocole standardisé. Une approche réaliste qui tient compte de votre rythme, de votre histoire et de votre vie d'aujourd'hui."
          },
          {
            name: "Préserver votre vitalité",
            description: "Vous accompagner pour traverser cette transition avec énergie, et non en subissant. L'objectif n'est pas de « maigrir à tout prix », mais de retrouver un équilibre durable."
          },
          {
            name: "Un suivi pour ancrer les résultats",
            description: "Des consultations régulières pour ajuster en fonction de l'évolution de vos symptômes, de vos cycles et de votre bien-être global."
          }
        ]
      },
      {
        title: "Pour qui est cet accompagnement ?",
        conditions: [
          "Femmes en début de préménopause (35–45 ans) qui souhaitent anticiper",
          "Femmes en préménopause active avec prise de poids inexpliquée",
          "Femmes traversant des bouffées de chaleur, troubles du sommeil ou irritabilité",
          "Femmes ménopausées souhaitant retrouver un équilibre métabolique",
          "Femmes après arrêt de la pilule, en transition hormonale",
          "Femmes souhaitant prévenir activement la santé osseuse et cardiovasculaire"
        ]
      }
    ],
    cta: {
      title: "Prête à traverser cette étape sereinement ?",
      text: "Réservez votre consultation au cabinet de Vevey ou de Clarens (Montreux). Un premier échange permet de comprendre où vous en êtes et de poser les bases d'un accompagnement adapté. Remboursement possible par certaines assurances complémentaires (ASCA & RME).",
      button: "Prendre rendez-vous"
    }
  },

  santeCardiovasculaire: {
    meta: {
      title: "Santé cardiovasculaire & prévention à Vevey, Clarens & Montreux",
      description: "Prévention cardiovasculaire et métabolique pour les femmes. Accompagnement pluridisciplinaire à Vevey, Clarens et Montreux : cholestérol, tension, glycémie, équilibre hormonal.",
      keywords: "santé cardiovasculaire femme, prévention cardio Vevey, cholestérol Clarens, tension Montreux, cardiologue nutritionniste Riviera"
    },
    h1: "Santé cardiovasculaire à Vevey, Clarens et Montreux : protéger votre cœur, à votre rythme",
    intro: "La santé cardiovasculaire de la femme est unique — et trop souvent sous-estimée. Les maladies cardiovasculaires restent la première cause de mortalité féminine, devant le cancer. Sur la Riviera, notre approche pluridisciplinaire associe nutrition, suivi métabolique et collaboration cardiologique pour vous accompagner dans une véritable prévention.",
    sections: [
      {
        title: "Pourquoi la santé cardiovasculaire est différente chez la femme",
        content: "Les hormones féminines jouent un rôle protecteur jusqu'à la ménopause. Mais cette protection s'estompe ensuite — et les facteurs de risque (tension, cholestérol, glycémie, inflammation) peuvent évoluer rapidement, parfois silencieusement. Chez la femme, les symptômes diffèrent de ceux des hommes, ce qui retarde souvent le diagnostic. Une prévention active, dès la quarantaine, change la trajectoire."
      },
      {
        title: "Vous vous reconnaissez si…",
        list: [
          "Vous avez un terrain familial cardiovasculaire (tension, cholestérol, infarctus)",
          "Vous entrez en préménopause ou en ménopause",
          "Vous prenez du poids, surtout au niveau abdominal",
          "Votre dernier bilan sanguin a montré un cholestérol ou une glycémie élevés",
          "Vous ressentez une fatigue inhabituelle, un essoufflement à l'effort",
          "Vous vivez avec un stress chronique difficile à apaiser",
          "Vous souhaitez agir en prévention, plutôt qu'en réaction"
        ]
      },
      {
        title: "Pourquoi une approche personnalisée fait la différence",
        subsections: [
          {
            subtitle: "Plusieurs leviers, jamais un seul",
            text: "Cholestérol, tension, glycémie, inflammation, poids, sommeil, stress, hormones : tous ces éléments s'influencent. Agir sur un seul facteur ne suffit jamais à protéger durablement le cœur."
          },
          {
            subtitle: "Une prévention adaptée à votre étape de vie",
            text: "Les besoins d'une femme de 40 ans, en préménopause ou ménopausée sont différents. Notre stratégie tient compte de votre profil hormonal et métabolique du moment."
          },
          {
            subtitle: "Un accompagnement pluridisciplinaire",
            text: "Nous travaillons en lien avec votre médecin ou cardiologue pour un suivi cohérent — la nutrition s'intègre dans une démarche médicale globale."
          }
        ]
      },
      {
        title: "Notre programme prévention cardio-métabolique",
        pillars: [
          {
            name: "Bilan personnalisé",
            description: "Anamnèse, lecture de vos analyses sanguines, analyse corporelle, identification de vos facteurs de risque spécifiques."
          },
          {
            name: "Stratégie nutritionnelle adaptée",
            description: "Construite sur mesure pour votre profil — sans régime restrictif, sans interdits inutiles, mais avec une vraie cohérence préventive."
          },
          {
            name: "Soutien métabolique",
            description: "Travail sur la stabilité glycémique, l'équilibre hormonal et l'inflammation — trois piliers majeurs de la santé cardiovasculaire féminine."
          },
          {
            name: "Suivi régulier",
            description: "Réévaluation périodique pour mesurer les progrès et ajuster en collaboration avec votre médecin référent."
          }
        ]
      },
      {
        title: "Pour qui est cet accompagnement ?",
        conditions: [
          "Femmes avec antécédents familiaux cardiovasculaires",
          "Femmes en préménopause ou ménopause",
          "Femmes avec hypertension, cholestérol élevé ou prédiabète",
          "Femmes en surpoids abdominal",
          "Femmes souhaitant agir en prévention active",
          "Femmes après un événement cardiovasculaire, en accompagnement médical"
        ]
      }
    ],
    cta: {
      title: "Protégez votre cœur, dès aujourd'hui",
      text: "Réservez votre consultation au cabinet de Vevey ou de Clarens (Montreux). Une démarche préventive est l'un des investissements santé les plus précieux. Consultations remboursées par certaines assurances complémentaires (ASCA & RME).",
      button: "Prendre rendez-vous"
    }
  },

  equilibreEmotionnel: {
    meta: {
      title: "Équilibre émotionnel & hypnopraxie à Vevey, Clarens & Montreux | Nutri Centre Riviera",
      description: "Compulsions, anxiété, fatigue émotionnelle, rapport douloureux à l'alimentation ? Accompagnement thérapeutique à Vevey, Clarens et Montreux : hypnopraxie & nutrition émotionnelle.",
      keywords: "hypnopraxie Vevey, hypnopraxie Clarens, hypnopraxie Montreux, équilibre émotionnel, compulsions alimentaires, gestion du stress Riviera, nutrition émotionnelle"
    },
    h1: "Équilibre émotionnel à Vevey, Clarens et Montreux : quand l'alimentation touche au plus intime",
    intro: "Compulsions sucrées, grignotages du soir, anxiété, irritabilité, stress chronique… Votre rapport à l'alimentation et à votre corps peut devenir source de souffrance — et aucun régime ne peut véritablement résoudre cela. Au Nutri Centre Riviera, à Vevey, Clarens et Montreux, nous proposons un accompagnement qui associe nutrition et hypnopraxie — une approche thérapeutique centrée sur les émotions et le fonctionnement intérieur.",
    sections: [
      {
        title: "Quand l'équilibre émotionnel passe aussi par l'assiette",
        content: "Notre intestin produit près de 90 % de notre sérotonine — l'un des neurotransmetteurs clés de l'humeur. Ce que vous mangez, votre microbiote, votre glycémie, votre sommeil et votre charge émotionnelle influencent directement votre sensation de bien-être. Mais chez de nombreuses femmes, l'alimentation devient aussi un refuge émotionnel : elle calme, rassure, comble un vide. Agir uniquement sur l'assiette ne suffit alors jamais. Il faut comprendre ce qui se joue en profondeur."
      },
      {
        title: "Vous vous reconnaissez dans ces signes ?",
        list: [
          "Compulsions alimentaires, surtout sucrées, souvent en fin de journée",
          "Un sentiment de perte de contrôle face à la nourriture",
          "Une culpabilité et une honte récurrentes après les repas",
          "Une fatigue mentale, une irritabilité, une anxiété qui s'installent",
          "Des troubles du sommeil, des réveils nocturnes",
          "Un stress chronique que vous n'arrivez plus à apaiser",
          "Un rapport douloureux à votre corps et à votre image",
          "Une sensibilité émotionnelle accrue, des larmes faciles"
        ]
      },
      {
        title: "Notre approche : corps, émotions, et fonctionnement intérieur",
        subsections: [
          {
            subtitle: "L'axe intestin-cerveau",
            text: "Votre microbiote communique en permanence avec votre cerveau. Un déséquilibre intestinal peut entretenir l'anxiété, la fatigue et les compulsions. Nous travaillons ce lien en profondeur, de manière personnalisée."
          },
          {
            subtitle: "La stabilité glycémique",
            text: "Les fluctuations de la glycémie impactent directement votre humeur et votre énergie. Un cadre nutritionnel adapté apporte déjà des changements rapides et visibles."
          },
          {
            subtitle: "L'hypnopraxie®, pour l'intime",
            text: "Méthode avancée de l'hypnose, l'hypnopraxie accède à ce que l'on « éprouve » au plus profond de soi. Elle est particulièrement efficace pour apaiser les compulsions, les blocages émotionnels et restaurer un rapport serein à soi."
          }
        ]
      },
      {
        title: "Ce sur quoi nous travaillons ensemble",
        pillars: [
          {
            name: "Stabiliser le terrain",
            description: "Rééquilibrer la glycémie et soutenir le microbiote — deux leviers puissants et souvent sous-estimés de la stabilité émotionnelle."
          },
          {
            name: "Apaiser les compulsions",
            description: "Comprendre ce que la nourriture vient combler, et proposer des réponses qui ne passent plus uniquement par l'assiette."
          },
          {
            name: "Soutenir les neurotransmetteurs",
            description: "Certaines carences nutritionnelles spécifiques entretiennent l'anxiété et la fatigue mentale. Un bilan ciblé permet d'agir précisément."
          },
          {
            name: "Libérer l'émotionnel en profondeur",
            description: "L'hypnopraxie permet d'accéder à ce qui, chez vous, alimente la compulsion ou la souffrance — pour un changement durable et intime."
          }
        ]
      },
      {
        title: "Pour qui est cet accompagnement ?",
        conditions: [
          "Compulsions alimentaires, grignotages émotionnels",
          "Rapport douloureux à l'image du corps",
          "Stress chronique, anxiété, irritabilité",
          "Fatigue mentale, baisse de motivation",
          "Troubles du sommeil liés à l'émotionnel",
          "Phases de transition (post-partum, préménopause, deuil, rupture)",
          "Envie de retrouver un rapport apaisé à soi et à la nourriture"
        ]
      }
    ],
    cta: {
      title: "Retrouvez un équilibre profond",
      text: "Un accompagnement qui prend en compte votre alimentation ET votre monde intérieur. Consultations au cabinet de Vevey ou de Clarens (Montreux). Remboursement possible par certaines assurances complémentaires (ASCA & RME).",
      button: "Prendre rendez-vous"
    }
  }
};
